import type { PrivateTenant, Tenant } from "@/types/tenant";

type TenantAliasIdentity = Pick<Tenant | PrivateTenant, "subDomain"> & {
  domain?: string | null;
  enterprise_tenant?: string | null;
  enterpriseTenant?: string | null;
};

export type TenantIdentity = TenantAliasIdentity | string | null | undefined;

const RIVAS_TENANT_SUBDOMAIN = "rivas";
const HEBE_TENANT_SUBDOMAIN = "hebe";
const RENU_TENANT_SUBDOMAIN = "renu";
const AGE_TENANT_SUBDOMAIN = "age";
const RENOVA_STAFF_PAY_TENANT_SUBDOMAINS = ["ra", "rth"] as const;
const BALANCED_HEALTH_AND_WELLNESS_TENANT_SUBDOMAIN = "bhw";
const RESTORX_TENANT_SUBDOMAIN = "restorx";

const DEFAULT_DOSE_COMMENT_LABEL = "comment";
const RIVAS_DOSE_COMMENT_LABEL = "MA assessment/comment";

export const RENU_SALES_TAX_DETAILS_TENANT_SUBDOMAINS = [RENU_TENANT_SUBDOMAIN];

const ALLURA_TENANT_SUBDOMAIN = "allura";
export const ALLURA_CASH_REGISTER_TENANT_SUBDOMAINS = [ALLURA_TENANT_SUBDOMAIN];

export const RIVAS_DETAILED_RECEIPT_DEFAULTS = {
  icd10Codes: [{ code: "E66.9", description: "Obesity, unspecified" }],
  cptCodes: [
    {
      code: "99213",
      description:
        "Office or other outpatient visit for the evaluation and management of an established patient",
    },
  ],
  customDescription: "TIN: 52-195-2051",
} as const;

function getTenantSubDomain(tenant: TenantIdentity): string | undefined {
  if (typeof tenant === "string") {
    return tenant.trim().toLowerCase();
  }

  return tenant?.subDomain?.trim().toLowerCase();
}

function isTenantSubDomain(tenant: TenantIdentity, subDomain: string): boolean {
  return getTenantSubDomain(tenant) === subDomain;
}

export function isEsspaTenant(tenant: TenantIdentity): boolean {
  if (typeof tenant === "string") {
    return isTenantSubDomain(tenant, "esspa");
  }

  return [
    tenant?.subDomain,
    tenant?.domain,
    tenant?.enterprise_tenant,
    tenant?.enterpriseTenant,
  ].some((identifier) => identifier?.trim().toLowerCase() === "esspa");
}

export function getDoseCommentLabel(tenant: TenantIdentity): string {
  return isTenantSubDomain(tenant, RIVAS_TENANT_SUBDOMAIN)
    ? RIVAS_DOSE_COMMENT_LABEL
    : DEFAULT_DOSE_COMMENT_LABEL;
}

export function shouldShowMedicationMissingFieldWarnings({
  tenant,
  medicationNoteId,
  noteIdsInChain,
}: {
  tenant: TenantIdentity;
  medicationNoteId?: string | null;
  noteIdsInChain: string[];
}): boolean {
  return (
    isTenantSubDomain(tenant, RIVAS_TENANT_SUBDOMAIN) &&
    !!medicationNoteId &&
    noteIdsInChain.includes(medicationNoteId)
  );
}

export function getDefaultDetailedReceiptMedicalDetails(
  tenant: TenantIdentity,
): typeof RIVAS_DETAILED_RECEIPT_DEFAULTS | null {
  return isTenantSubDomain(tenant, RIVAS_TENANT_SUBDOMAIN)
    ? RIVAS_DETAILED_RECEIPT_DEFAULTS
    : null;
}

export function shouldShowFilledFormChecklistItem({
  tenant,
  hasRequiredForms,
}: {
  tenant: TenantIdentity;
  hasRequiredForms: boolean;
}): boolean {
  return hasRequiredForms || isTenantSubDomain(tenant, HEBE_TENANT_SUBDOMAIN);
}

export function shouldShowBillingDashboardCommentFilter(
  tenant: TenantIdentity,
): boolean {
  return isTenantSubDomain(tenant, HEBE_TENANT_SUBDOMAIN);
}

// The patient-chart Identification card (overview + sidebar) is currently
// enabled only for this tenant. It still only renders when the patient
// actually has an ID document on file.
export function showsPatientIdentificationCard(
  tenant: TenantIdentity,
): boolean {
  return isTenantSubDomain(tenant, AGE_TENANT_SUBDOMAIN);
}

// OneBlood Therapeutic Phlebotomy orders are a one-tenant feature — gated
// here rather than via the Module system, which would mean editing four
// files plus an ops-dashboard toggle for a single tenant. The backend stays
// ungated beyond patients:documents:read; see decoda/common/tenant_flags.py.
//
// Uses the allowlist helper rather than a bare subdomain equality check so the
// feature is reachable on any tenant in local development — a branch dev tenant
// is never named "age", so a strict check would make this untestable without
// hand-creating one.
export function createsPhlebotomyOrders(tenant: TenantIdentity): boolean {
  return tenantMatchesAllowedSubDomains({
    tenantSubDomain: getTenantSubDomain(tenant),
    allowedSubDomains: [AGE_TENANT_SUBDOMAIN],
  });
}

// Age Rejuvenation's front-desk phone and fax, printed on the Form-96
// physician block. Neither is reachable through platform data: the clinic
// never answers the line we provisioned them, `Location.phone_number` has no
// editor in the console, and the fax resolver only reads provisioned numbers.
const AGE_PHLEBOTOMY_ORDER_CONTACT = {
  phone: "8135589500",
  fax: "8134365155",
} as const;

export function getPhlebotomyOrderContact(
  tenant: TenantIdentity,
): typeof AGE_PHLEBOTOMY_ORDER_CONTACT | null {
  return tenantMatchesAllowedSubDomains({
    tenantSubDomain: getTenantSubDomain(tenant),
    allowedSubDomains: [AGE_TENANT_SUBDOMAIN],
  })
    ? AGE_PHLEBOTOMY_ORDER_CONTACT
    : null;
}

// Before checkout can reach the payment step, staff see every line on the
// ticket with its quantity and have to confirm the amounts are right.
//
// One clinic only. Balanced Health and Wellness were undercharging by
// $600-$800 a visit because providers are still charting when the patient
// reaches the front desk, so the desk rings up what it can see. Everywhere
// else this is just an extra click between the ticket and payment, which is
// why it is hard-coded here rather than exposed as a setting.
//
// Deliberately a bare subdomain equality check, NOT
// `tenantMatchesAllowedSubDomains`: that helper returns true for every tenant
// when NODE_ENV === "development", and this prompt must never appear for a
// clinic that did not ask for it. Previewing it locally is done by pointing a
// dev tenant's subdomain at "bhw", not by widening the gate.
//
// Matches on subDomain only. The alias fields `domain` / `enterpriseTenant`
// that `isEsspaTenant` also consults are intentionally not consulted here —
// an enterprise parent must not pull the prompt into its sibling clinics.
export function usesCheckoutConfirmationPrompt(
  tenant: TenantIdentity,
): boolean {
  return isTenantSubDomain(
    tenant,
    BALANCED_HEALTH_AND_WELLNESS_TENANT_SUBDOMAIN,
  );
}

// Restorx's self-schedule flow has no patient-type (new/existing) choice.
export function skipsSelfSchedulePatientTypeChoice(
  tenant: TenantIdentity,
): boolean {
  return isTenantSubDomain(tenant, RESTORX_TENANT_SUBDOMAIN);
}

// Only Restorx collects patient profile details after self-schedule authentication.
export function usesSelfSchedulePatientDetails(
  tenant: TenantIdentity,
): boolean {
  return isTenantSubDomain(tenant, RESTORX_TENANT_SUBDOMAIN);
}

export function usesRenovaStaffPayReport(tenant: TenantIdentity): boolean {
  const subDomain = getTenantSubDomain(tenant);
  return RENOVA_STAFF_PAY_TENANT_SUBDOMAINS.some(
    (renovaSubDomain) => subDomain === renovaSubDomain,
  );
}

export function tenantMatchesAllowedSubDomains({
  tenantSubDomain,
  allowedSubDomains,
  includeAllInDevelopment = true,
}: {
  tenantSubDomain?: string | null;
  allowedSubDomains?: readonly string[];
  includeAllInDevelopment?: boolean;
}): boolean {
  if (!allowedSubDomains || allowedSubDomains.length === 0) return true;
  if (includeAllInDevelopment && process.env.NODE_ENV === "development") {
    return true;
  }
  if (!tenantSubDomain) return false;

  const normalizedTenantSubDomain = tenantSubDomain.trim().toLowerCase();
  return allowedSubDomains.some(
    (subDomain) => normalizedTenantSubDomain === subDomain.trim().toLowerCase(),
  );
}
